<script setup lang="ts">
import PracticeDetailBody from "@/components/models/practice/detail/PracticeDetailBody.vue";
import { useAbility } from "@/api/models/auth/ability";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { definePage } from "unplugin-vue-router/runtime";
import OrganizationSectionHero from "@/components/shared/headers/OrganizationSectionHero.vue";
import Breadcrumbs from "@/components/shared/Breadcrumbs.vue";
import { ORGANIZATION_ACCENTS } from "@/components/shared/navbar/useNavbarSubItems";
import { formatAddress } from "@/utils/Formatters";
import { usePracticeDetail } from "@/composables/models/practice/usePracticeMe";
import { useCompanyDetail } from "@/composables/models/company/useCompanyMe";
import { ECompanyType } from "@/api/models/company/company";
import {
  ORGANIZATION_LEVEL_PATHS,
  useMyOrganization,
} from "@/composables/shared/useMyOrganization";

definePage({
  meta: {
    title: "generic.action.viewEntity",
    titleParams: { entity: "generic.entity.practice" },
    checkPrivileges: () => {
      const { can } = useAbility();
      return (
        can($permActions.READ, $permSubjects.PRACTICE__PRACTICE) ||
        can($permActions.PRACTICE_ME_VIEW, $permSubjects.PRACTICE__PRACTICE)
      );
    },
  },
});

const route = useRoute();

const practiceId = computed(() => (route.params as { id: string }).id);
const useMe = computed(() => route.query.scope === "me");

const { data: practice, isLoading: loadingPractice } = usePracticeDetail(
  { id: practiceId },
  { useMe, redirectOnNotFound: true }
);

const companyId = computed(() => {
  const company = practice.value?.company;
  return typeof company === "string" ? company : company?.id;
});
const { data: company, isLoading: loadingCompany } = useCompanyDetail({ id: companyId }, { useMe });

const { t } = useI18n();
const router = useRouter();

const { msos } = useMyOrganization();

const ownerMso = computed(() => (msos.value.length === 1 ? msos.value[0] : undefined));
const ownerName = computed(() => ownerMso.value?.name);

const { can } = useAbility();

const canEdit = computed(
  () =>
    !!practice.value &&
    (can(
      $permActions.UPDATE,
      $permSubjects.PRACTICE__PRACTICE,
      practice.value.user_permissions,
      "any"
    ) ||
      can(
        $permActions.PRACTICE_ME_EDIT,
        $permSubjects.PRACTICE__PRACTICE,
        practice.value.user_permissions,
        "any"
      ))
);

const handleEdit = () => {
  if (!practice.value) return;

  router.push({
    name: "/practices/[id].edit",
    params: { id: practice.value.id },
    query: {
      type: company.value?.type === ECompanyType.GP_PRACTICES ? "gp" : "ortho",
      ...(route.query.scope === "me" ? { scope: "me" } : {}),
    },
  });
};

const breadcrumbItems = computed(() => [
  {
    label: ownerName.value ?? t("generic.entity.practice", 2),
    to: ORGANIZATION_LEVEL_PATHS.offices,
  },
  {
    label: company.value?.name ?? "",
    to: company.value
      ? { name: "/companies/[id].[[tab]]" as const, params: { id: company.value.id } }
      : undefined,
  },
  { label: practice.value?.name ?? "" },
]);

const heroEyebrow = computed(() =>
  company.value?.name
    ? t("models.practice.officeWithin", { entity: company.value.name })
    : t("generic.entity.practice", 1)
);

const heroStats = computed(() => {
  const stats = [{ label: t("generic.entity.company"), value: company.value?.name || "—" }];
  const address = practice.value?.addresses?.[0];

  if (address) {
    stats.push({ label: t("generic.fields.address"), value: formatAddress(address) });
  }

  return stats;
});
</script>

<template>
  <v-skeleton-loader
    :loading="loadingPractice || loadingCompany"
    type="heading,card"
  >
    <template v-if="practice">
      <breadcrumbs :items="breadcrumbItems" />

      <organization-section-hero
        :practice="practice"
        :eyebrow="heroEyebrow"
        :title="practice.name"
        :accent-color="ORGANIZATION_ACCENTS.offices"
        icon="mdi-tooth-outline"
        :stats="heroStats"
        :show-edit="canEdit"
        edit-test-id="practice-detail-edit-button"
        @edit="handleEdit"
      />

      <practice-detail-body
        :practice="practice"
        :company="company"
      />
    </template>
  </v-skeleton-loader>
  <router-view />
</template>
